"use client";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import CompactRideList from "./CompactRideList";
import { isRidePoint, selectNearbyRides, type ExploreWhen, type NearbyRide, type RidePoint } from "@/lib/homepageRideModes";

type Props = { map: LeafletMap | null; pickup: RidePoint | null; onUseDestination: (point: RidePoint) => void };
export default function NearbyRidesPanel({ map, pickup, onUseDestination }: Props) {
  const [rides, setRides] = useState<NearbyRide[]>([]);
  const [when, setWhen] = useState<ExploreWhen>("today");
  const [radius, setRadius] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needsLogin, setNeedsLogin] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const lastViewRef = useRef<{ map: LeafletMap; key: string } | null>(null);
  const matches = selectNearbyRides(rides, pickup, when, radius, now);
  const shown = matches.slice(0, 60);
  const selected = shown.find(ride => ride.id === selectedId);
  // Effects depend on source inputs, not the newly allocated `shown` array.
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true); setError(""); setNeedsLogin(false); setRides([]); setSelectedId(null);
      try {
        const response = await fetch("/api/rides/nearby", { cache: "no-store", signal: controller.signal });
        if (response.status === 401) { if (!controller.signal.aborted) setNeedsLogin(true); return; }
        const data = await response.json();
        if (!response.ok || !Array.isArray(data.rides)) throw new Error(data.message || "Unable to load nearby rides.");
        if (!controller.signal.aborted) { setRides(data.rides); setNow(new Date()); }
      } catch (cause) { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Unable to load nearby rides."); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    }
    void load();
    return () => controller.abort();
  }, [refresh]);
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    let cancelled = false;
    let layer: import("leaflet").LayerGroup | null = null;
    if (!map || !isRidePoint(pickup)) return;
    const visible = selectNearbyRides(rides, pickup, when, radius, now).slice(0, 60);
    void import("leaflet").then(L => {
      if (cancelled) return;
      layer = L.layerGroup().addTo(map);
      const area = L.circle([pickup.latitude, pickup.longitude], { radius: radius * 1000, color: "#4f46e5", weight: 2, fillOpacity: 0.05, interactive: false }).addTo(layer);
      for (const ride of visible) {
        if (ride.route.length >= 2) {
          L.polyline(ride.route, { color: selectedId === ride.id ? "#16a34a" : "#94a3b8", weight: selectedId === ride.id ? 4 : 2, opacity: selectedId === ride.id ? 0.85 : 0.4, interactive: false }).addTo(layer);
        }
        if (ride.latitude === null || ride.longitude === null) continue;
        const marker = L.circleMarker([ride.latitude, ride.longitude], { radius: selectedId === ride.id ? 9 : 6, color: "white", weight: 2, fillColor: "#16a34a", fillOpacity: 1, bubblingMouseEvents: false }).addTo(layer);
        const popup = document.createElement("div"); popup.textContent = `${ride.name}: ${ride.from} → ${ride.to} · ${ride.seats} seats`;
        marker.bindPopup(popup).on("click", () => setSelectedId(ride.id));
      }
      // Updating time windows removes expired rows without undoing the user's map pan.
      const viewKey = `${pickup.latitude}:${pickup.longitude}:${when}:${radius}:${selectedId}`;
      if (lastViewRef.current?.map !== map || lastViewRef.current.key !== viewKey) {
        const focused = visible.find(ride => ride.id === selectedId);
        if (focused && focused.route.length >= 2) map.fitBounds(L.latLngBounds(focused.route), { padding: [30, 30], maxZoom: 13 });
        else map.fitBounds(area.getBounds(), { padding: [25, 25], maxZoom: 13 });
        lastViewRef.current = { map, key: viewKey };
      }
    }).catch(() => { if (!cancelled) setError("Map routes could not load. You can still use the ride list."); });
    return () => { cancelled = true; layer?.remove(); };
  }, [map, pickup, rides, when, radius, selectedId, now]);
  return <div className="space-y-2">
    <p className="text-xs text-slate-600">Explore drivers whose routes pass near your selected pickup.</p>
    <div className="flex flex-wrap items-center gap-2">
      <div aria-label="Nearby ride time" className="flex gap-1">{(["now", "today", "tomorrow"] as const).map(value => <button type="button" key={value} aria-pressed={when === value} onClick={() => { setWhen(value); setSelectedId(null); setNow(new Date()); }} className={`rounded border px-2 py-1 text-xs capitalize ${when === value ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200"}`}>{value}</button>)}</div>
      <div aria-label="Nearby ride radius" className="flex gap-1">{[5, 10, 20].map(value => <button type="button" key={value} aria-pressed={radius === value} onClick={() => { setRadius(value); setSelectedId(null); }} className={`rounded border px-2 py-1 text-xs ${radius === value ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200"}`}>{value} km</button>)}</div>
      <button type="button" disabled={loading} onClick={() => setRefresh(n => n + 1)} className="text-xs font-semibold text-indigo-700 disabled:opacity-50">Refresh</button>
    </div>
    {!isRidePoint(pickup) && <p className="text-xs text-slate-500">Select a pickup using the top search box or GPS to explore nearby rides.</p>}
    {loading && <p role="status" className="text-xs text-slate-500">Loading nearby rides...</p>}
    {needsLogin && <Link href="/login" className="text-xs font-semibold text-indigo-700 underline">Log in to see nearby rides</Link>}
    {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
    {!loading && !error && !needsLogin && isRidePoint(pickup) && <p className="text-xs text-slate-500">{shown.length ? `${shown.length} rides shown${matches.length > 60 ? " · More rides available; narrow your radius" : ""}` : "No rides in this radius and time window."}</p>}
    <CompactRideList rides={shown} onSelect={ride => setSelectedId(ride.id)} />
    {selected && <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-2 text-xs">
      <p className="truncate font-semibold">{selected.name}: {selected.from} → {selected.to}</p>
      <p className="mt-1 text-slate-600">Route passes approximately {selected.nearbyDistanceKm.toFixed(1)} km from your pickup.</p>
      {selected.destination ? <button type="button" onClick={() => { if (selected.destination) onUseDestination(selected.destination); }} className="mt-2 font-semibold text-indigo-700 underline">Search this destination / Review booking</button> : <p className="mt-1 text-slate-600">Choose a destination in Search Existing Ride to check matching rides.</p>}
    </div>}
  </div>;
}
